import { createFileRoute, Link } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { convexQuery } from '@convex-dev/react-query'
import { useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import * as React from 'react'

export const Route = createFileRoute('/d/$docId')({
  component: DocPage,
})

function DocPage() {
  const { docId } = Route.useParams()
  const { data: doc } = useSuspenseQuery(convexQuery(api.docs.getDoc, { docId: docId as any }))
  const { data: pages } = useSuspenseQuery(convexQuery(api.pages.listPages, { docId: docId as any }))
  const { data: presence } = useSuspenseQuery(convexQuery(api.presence.list, { docId: docId as any }))
  const { data: comments } = useSuspenseQuery(convexQuery(api.comments.list, { docId: docId as any }))
  const { data: jobs } = useSuspenseQuery(convexQuery(api.jobs.listJobs, { docId: docId as any }))

  const updateDoc = useMutation(api.docs.updateDocContent)
  const scrape = useAction(api.ingest.scrapeUrl)
  const ping = useMutation(api.presence.ping)
  const addComment = useMutation(api.comments.add)
  const publish = useMutation(api.docs.publishDoc)

  const [content, setContent] = React.useState(doc?.content ?? '')
  const [title, setTitle] = React.useState(doc?.title ?? '')
  const [url, setUrl] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [showSources, setShowSources] = React.useState(false)
  const [showComments, setShowComments] = React.useState(false)
  const [showPublishModal, setShowPublishModal] = React.useState(false)
  const [publishSlug, setPublishSlug] = React.useState('')

  React.useEffect(() => {
    setContent(doc?.content ?? '')
    setTitle(doc?.title ?? '')
  }, [doc?._id])

  React.useEffect(() => {
    void ping({ docId: docId as any })
    const id = setInterval(() => void ping({ docId: docId as any }), 10_000)
    return () => clearInterval(id)
  }, [docId])

  // Auto-save
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (content !== doc?.content || title !== doc?.title) {
        setIsSaving(true)
        updateDoc({ docId: docId as any, content, title }).finally(() => setIsSaving(false))
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [content, title])

  const onPublish = async () => {
    if (!publishSlug.trim()) return
    await publish({ docId: docId as any, slug: publishSlug.trim() })
    setShowPublishModal(false)
  }

  const onScrape = async () => {
    if (!url.trim()) return
    setBusy(true)
    try {
      await scrape({ docId: docId as any, url: url.trim() })
      setUrl('')
    } catch (e: any) {
      console.error(e)
    } finally {
      setBusy(false)
    }
  }

  if (!doc) return null

  return (
    <div className="flex h-screen bg-[#fafafa]">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <Link to="/app" className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Weblens
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <button
            onClick={() => setShowSources(!showSources)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md ${showSources ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Sources ({pages.length})
          </button>
          <button
            onClick={() => setShowComments(!showComments)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md ${showComments ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Comments ({comments.length})
          </button>
          <div className="pt-4 border-t border-gray-200 mt-4">
            <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              {presence.length} online
            </div>
          </div>
        </nav>
        <div className="p-3 border-t border-gray-200">
          {doc.publishedSlug ? (
            <Link
              to={`/publish/${doc.publishedSlug}` as any}
              className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View Published
            </Link>
          ) : (
            <button
              onClick={() => {
                const base = (doc.title || '').trim() || 'untitled'
                const slug = base
                  .toLowerCase()
                  .replace(/[^a-z0-9\s-]/g, '')
                  .trim()
                  .replace(/\s+/g, '-')
                  .slice(0, 64)
                setPublishSlug(slug || '')
                setShowPublishModal(true)
              }}
              className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Publish
            </button>
          )}
        </div>
      </aside>

      {/* Main Editor */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/w/${doc.workspaceId}` as any} className="text-sm text-gray-600 hover:text-gray-900">
              ← Back
            </Link>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {isSaving ? (
                <span className="flex items-center gap-1">
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Saved
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSources(!showSources)}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Sources
            </button>
            <button
              onClick={() => setShowComments(!showComments)}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              {comments.length}
            </button>
          </div>
        </header>

        {/* Editor Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-24 py-16">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="w-full text-5xl font-bold border-none outline-none bg-transparent mb-4 text-gray-900 placeholder-gray-300"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Start writing..."
              className="w-full min-h-[600px] text-lg border-none outline-none bg-transparent resize-none leading-relaxed text-gray-900 placeholder-gray-300"
            />
          </div>
        </div>
      </main>

      {/* Right Sidebar - Sources */}
      {showSources && (
        <aside className="w-80 bg-white border-l border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Sources</h3>
            <button onClick={() => setShowSources(false)} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="space-y-2">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                disabled={busy}
                onClick={() => void onScrape()}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {busy ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Scraping...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Source
                  </>
                )}
              </button>
            </div>
            {jobs.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Recent Jobs</h4>
                {jobs.map((job: any) => (
                  <div key={job._id} className="p-3 bg-gray-50 rounded-md text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      {job.status === 'running' && (
                        <svg className="animate-spin h-3 w-3 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {job.status === 'done' && (
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {job.status === 'error' && (
                        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="font-medium text-gray-700 capitalize">{job.status}</span>
                    </div>
                    <a href={job.url} target="_blank" className="text-xs text-blue-600 hover:underline truncate block">
                      {job.url}
                    </a>
                    {job.error && <p className="text-xs text-red-600 mt-1">{job.error}</p>}
                  </div>
                ))}
              </div>
            )}
            {pages.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Stored Pages</h4>
                {pages.map((p: any) => (
                  <a
                    key={p._id}
                    href={p.url}
                    target="_blank"
                    className="block p-3 bg-gray-50 hover:bg-gray-100 rounded-md text-sm text-blue-600 hover:underline truncate"
                  >
                    {p.url}
                  </a>
                ))}
              </div>
            )}
            {pages.length === 0 && jobs.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <p className="text-sm">No sources yet</p>
                <p className="text-xs mt-1">Add a URL to scrape content</p>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Right Sidebar - Comments */}
      {showComments && (
        <aside className="w-80 bg-white border-l border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Comments</h3>
            <button onClick={() => setShowComments(false)} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {comments.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <p className="text-sm">No comments yet</p>
                <p className="text-xs mt-1">Start a conversation</p>
              </div>
            ) : (
              comments.map((c: any) => (
                <div key={c._id} className="p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-900">{c.text}</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(c.createdAt).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
          <div className="p-4 border-t border-gray-200">
            <CommentInput onSubmit={async (text) => { await addComment({ docId: docId as any, text }) }} />
          </div>
        </aside>
      )}

      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Publish document</h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose a public URL slug for this document. You can share this link with anyone.
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <span className="truncate">/publish/</span>
              <input
                autoFocus
                value={publishSlug}
                onChange={(e) => setPublishSlug(e.target.value)}
                placeholder="my-report"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPublishModal(false)
                  setPublishSlug('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!publishSlug.trim()}
                onClick={() => void onPublish()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CommentInput({ onSubmit }: { onSubmit: (text: string) => Promise<void> }) {
  const [value, setValue] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  return (
    <div className="flex gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add a comment..."
        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        disabled={busy || !value.trim()}
        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={async () => {
          setBusy(true)
          try {
            await onSubmit(value.trim())
            setValue('')
          } finally {
            setBusy(false)
          }
        }}
      >
        Send
      </button>
    </div>
  )
}
